import './PsychBoard.css';
import React, { useState } from 'react';
import * as XLSX from 'xlsx';

function PsychBoard({}) {
    const sliderGroups = {
        'Psychosis': ['Auditory Hallucinations', 'Visual Hallucinations', 'Tactile Hallucinations', 'Ideas of Reference', 'Mind Reading', 'Delusions', 'Thought Broadcasting'],
        'Mania': ['Speed of Ideas', 'Risk Taking Behavior', 'Energy Levels', 'Sleep Issues']
    };

    const allLabels = [...Object.values(sliderGroups).flat(), 'Paranoia'];

    const [sliderValues, setSliderValues] = useState(
        allLabels.reduce((acc, label) => ({ ...acc, [label]: 0 }), {})
    );

    const [isRandomizing, setIsRandomizing] = useState(false);
    const [isDJing, setIsDJing] = useState(false);
    const [currentRandomizingSlider, setCurrentRandomizingSlider] = useState(null);
    const [showExportPopup, setShowExportPopup] = useState(false);
    const [exportName, setExportName] = useState('');

    // Audio context for sound effects
    const audioContextRef = React.useRef(null);

    // Initialize audio context
    React.useEffect(() => {
        try {
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        } catch (error) {
            console.log('Web Audio API not supported');
        }
    }, []);

    // Function to play mixer board sound
    const playMixerSound = (value, isDJMode = false) => {
        if (!audioContextRef.current) return;

        try {
            const ctx = audioContextRef.current;
            
            // Resume audio context if suspended (required for user interaction)
            if (ctx.state === 'suspended') {
                ctx.resume();
            }

            const oscillator = ctx.createOscillator();
            const gainNode = ctx.createGain();
            const filterNode = ctx.createBiquadFilter();

            // Connect nodes
            oscillator.connect(filterNode);
            filterNode.connect(gainNode);
            gainNode.connect(ctx.destination);

            // Configure oscillator - frequency based on slider value
            const baseFreq = isDJMode ? 200 : 150;
            const frequency = baseFreq + (value * 50); // Higher values = higher pitch
            oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
            oscillator.type = 'sawtooth'; // Mixer-like sound

            // Configure filter for that mixer board feel
            filterNode.type = 'lowpass';
            filterNode.frequency.setValueAtTime(1000 + (value * 200), ctx.currentTime);
            filterNode.Q.setValueAtTime(2, ctx.currentTime);

            // Configure gain envelope
            const duration = isDJMode ? 0.1 : 0.05;
            const volume = isDJMode ? 0.15 : 0.08;
            
            gainNode.gain.setValueAtTime(0, ctx.currentTime);
            gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.01);
            gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

            // Start and stop
            oscillator.start(ctx.currentTime);
            oscillator.stop(ctx.currentTime + duration);

        } catch (error) {
            console.log('Audio playback error:', error);
        }
    };

    const handleExportResults = () => {
        if (!exportName.trim()) return;

        // Prepare data for Excel
        const currentDate = new Date().toLocaleDateString();
        const data = [];

        // Add header information
        data.push(['Psychboard Results']);
        data.push(['Name:', exportName]);
        data.push(['Date:', currentDate]);
        data.push(['Threshold Value:', thresholdValue]);
        data.push([]);

        // Add category results
        Object.entries(sliderGroups).forEach(([groupName, labels]) => {
            data.push([groupName]);
            labels.forEach(label => {
                data.push(['', label, sliderValues[label]]);
            });
            data.push([]);
        });

        // Add Paranoia separately
        data.push(['Paranoia']);
        data.push(['', 'Paranoia', sliderValues['Paranoia']]);
        data.push([]);

        // Add warning status
        if (shouldShowWarning()) {
            data.push(['Warning Status:', getWarningText()]);
        } else {
            data.push(['Warning Status:', 'No warnings']);
        }

        // Create workbook and worksheet
        const wb = XLSX.utils.book_new();
        const ws = XLSX.utils.aoa_to_sheet(data);

        // Style the worksheet (basic styling)
        ws['!cols'] = [
            { wch: 20 }, // Column A width
            { wch: 25 }, // Column B width
            { wch: 10 }  // Column C width
        ];

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Psychboard Results');

        // Generate filename with name and date
        const filename = `Psychboard_${exportName.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`;

        // Download the file
        XLSX.writeFile(wb, filename);

        // Close popup and reset name
        setShowExportPopup(false);
        setExportName('');
    };

    const openExportPopup = () => {
        setShowExportPopup(true);
    };

    const closeExportPopup = () => {
        setShowExportPopup(false);
        setExportName('');
    };

    const handleSliderChange = (label, value) => {
        setSliderValues(prev => ({
            ...prev,
            [label]: parseInt(value)
        }));
        
        // Play mixer sound when slider is manually adjusted
        if (!isDJing) {
            playMixerSound(parseInt(value));
        }
    };

    const resetBoard = () => {
        setSliderValues(
            allLabels.reduce((acc, label) => ({ ...acc, [label]: 0 }), {})
        );
    };

    const letDopamineBeTheDJ = async () => {
        setIsDJing(true);
        const shuffledLabels = [...allLabels].sort(() => Math.random() - 0.5);
        
        for (let i = 0; i < shuffledLabels.length; i++) {
            const label = shuffledLabels[i];
            setCurrentRandomizingSlider(label);
            
            // Random value between 0 and 10
            const randomValue = Math.floor(Math.random() * 11);
            
            setSliderValues(prev => ({
                ...prev,
                [label]: randomValue
            }));
            
            // Play DJ mode sound effect
            playMixerSound(randomValue, true);
            
            // Wait between each slider adjustment
            await new Promise(resolve => setTimeout(resolve, 300));
        }
        
        setCurrentRandomizingSlider(null);
        setIsDJing(false);
    };

    // Check if any values are greater than 0
    const hasNonZeroValues = () => {
        return Object.values(sliderValues).some(value => value > 0);
    };

    // Calculate threshold value based on formula
    const calculateThresholdValue = () => {
        // Get all values except paranoia for the main calculations
        const nonParanoiaValues = Object.entries(sliderValues)
            .filter(([key]) => key !== 'Paranoia')
            .map(([, value]) => value);
            
        const valuesAtOrAbove5 = nonParanoiaValues.filter(value => value >= 5).length;
        const valuesAtOrAbove7 = nonParanoiaValues.filter(value => value >= 7).length;
        const valuesAtOrAbove9 = nonParanoiaValues.filter(value => value >= 9).length;
        const paranoiaValue = sliderValues['Paranoia'] || 0;
        
        // Base calculation
        let threshold = 0;
        
        if (valuesAtOrAbove5 === 1) threshold = 1;
        if (valuesAtOrAbove5 === 2) threshold = 3;
        if (valuesAtOrAbove5 >= 3) threshold = 5;
        
        // Additional increments for higher severity values
        if (valuesAtOrAbove7 >= 1) threshold += 1;
        if (valuesAtOrAbove7 >= 2) threshold += 1;
        if (valuesAtOrAbove7 >= 3) threshold += 1;
        
        // Further increments for extreme values
        if (valuesAtOrAbove9 >= 1) threshold += 1;
        if (valuesAtOrAbove9 >= 2) threshold += 2;
        
        // Paranoia contribution: +1 for every 2 points (10 paranoia = +5 threshold)
        threshold += Math.floor(paranoiaValue / 2);
        
        // Cap at maximum of 10
        return Math.min(threshold, 10);
    };

    const thresholdValue = calculateThresholdValue();

    // Check if warning should be shown
    const shouldShowWarning = () => {
        return thresholdValue >= 4;
    };

    const getWarningType = () => {
        if (thresholdValue >= 5) return 'red';
        if (thresholdValue === 4) return 'yellow';
        return null;
    };

    const getWarningText = () => {
        if (thresholdValue >= 5) return 'High probability approaching psychosis';
        if (thresholdValue === 4) return 'Slight probability approaching psychosis';
        return '';
    };

    return (
        <div className='Psych-Board-Frame'>
            {/* Threshold Dial - Fixed Top Left */}
            <div className='threshold-dial-container'>
                <div className='threshold-dial'>
                    <div 
                        className='dial-pointer'
                        style={{ 
                            transform: `translateX(-50%) rotate(${(thresholdValue / 10) * 360}deg)` 
                        }}
                    />
                    <div className='dial-center'>
                        {thresholdValue}
                    </div>
                </div>
                <div className='dial-label'>Threshold for psychosis</div>
            </div>

            {shouldShowWarning() && (
                <div className={`warning-container ${getWarningType()}`}>
                    <span className='warning-icon'>⚠️</span>
                    <span className={`warning-text ${getWarningType()}`}>{getWarningText()}</span>
                </div>
            )}
            
            <h2>Psychboard</h2>

            <div className='board-container'>
                {Object.entries(sliderGroups).map(([groupName, labels]) => (
                    <div key={groupName} className='slider-section'>
                        <h3 className='section-title'>{groupName}</h3>
                        <div className='sliders-container'>
                            {labels.map((label) => (
                                <div key={label} className={`slider-group ${currentRandomizingSlider === label ? 'randomizing' : ''}`}>
                                    <span className='slider-value'>{sliderValues[label]}</span>
                                    <div className='slider-container'>
                                        <div className='slider-tick-marks'>
                                            <div className='slider-tick major'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick major'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick major'></div>
                                        </div>
                                        <div className='slider-tick-numbers'>
                                            <span className='slider-tick-number'>10</span>
                                            <span className='slider-tick-number'>8</span>
                                            <span className='slider-tick-number'>6</span>
                                            <span className='slider-tick-number'>4</span>
                                            <span className='slider-tick-number'>2</span>
                                            <span className='slider-tick-number'>0</span>
                                        </div>
                                        <input
                                            type='range'
                                            min='0'
                                            max='10'
                                            value={sliderValues[label]}
                                            onChange={(e) => handleSliderChange(label, e.target.value)}
                                            className='slider'
                                            orient="vertical"
                                            disabled={isDJing}
                                        />
                                        <div className='slider-tick-numbers'>
                                            <span className='slider-tick-number'>10</span>
                                            <span className='slider-tick-number'>8</span>
                                            <span className='slider-tick-number'>6</span>
                                            <span className='slider-tick-number'>4</span>
                                            <span className='slider-tick-number'>2</span>
                                            <span className='slider-tick-number'>0</span>
                                        </div>
                                        <div className='slider-tick-marks'>
                                            <div className='slider-tick major'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick major'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick'></div>
                                            <div className='slider-tick major'></div>
                                        </div>
                                    </div>
                                    <label className='slider-label'>{label}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            
            {/* Paranoia Slider */}
            <div className='paranoia-section'>
                <h3 className='paranoia-title'>Paranoia</h3>
                <div className='paranoia-content'>
                    <div className={`paranoia-slider-wrapper ${currentRandomizingSlider === 'Paranoia' ? 'randomizing' : ''}`}>
                        <span className='paranoia-value'>{sliderValues['Paranoia']}</span>
                        <div className='paranoia-slider-container'>
                            <div className='paranoia-tick-marks'>
                                <div className='paranoia-tick major'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick major'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick major'></div>
                            </div>
                            <div className='paranoia-tick-numbers'>
                                <span className='paranoia-tick-number'>0</span>
                                <span className='paranoia-tick-number'>2</span>
                                <span className='paranoia-tick-number'>4</span>
                                <span className='paranoia-tick-number'>6</span>
                                <span className='paranoia-tick-number'>8</span>
                                <span className='paranoia-tick-number'>10</span>
                            </div>
                            <input
                                type='range'
                                min='0'
                                max='10'
                                value={sliderValues['Paranoia']}
                                onChange={(e) => handleSliderChange('Paranoia', e.target.value)}
                                className='paranoia-slider'
                                disabled={isDJing}
                            />
                            <div className='paranoia-tick-numbers'>
                                <span className='paranoia-tick-number'>0</span>
                                <span className='paranoia-tick-number'>2</span>
                                <span className='paranoia-tick-number'>4</span>
                                <span className='paranoia-tick-number'>6</span>
                                <span className='paranoia-tick-number'>8</span>
                                <span className='paranoia-tick-number'>10</span>
                            </div>
                            <div className='paranoia-tick-marks'>
                                <div className='paranoia-tick major'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick major'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick'></div>
                                <div className='paranoia-tick major'></div>
                            </div>
                        </div>
                        <label className='paranoia-label'>Paranoia</label>
                    </div>
                </div>
            </div>
            
            {/* Export Results Button */}
            <button 
                className='export-button'
                onClick={openExportPopup}
                disabled={isDJing}
            >
                📊 Export Results
            </button>
            
            {/* Dopamine DJ Button */}
            <button 
                className={`dopamine-button ${isDJing ? 'djing' : ''}`}
                onClick={letDopamineBeTheDJ}
                disabled={isDJing}
            >
                {isDJing ? '🎵 Dopamine is DJing 🎵' : '🎧 Let Dopamine be the DJ 🎧'}
            </button>
            
            {/* Reset Button - Only show when values > 0 */}
            {hasNonZeroValues() && (
                <button 
                    className='reset-button' 
                    onClick={resetBoard}
                    disabled={isDJing}
                >
                    Reset Board
                </button>
            )}

            {/* Reference Citation */}
            <div className='reference-citation'>
                Heacock, C. (Host). (2022, October 6). Psychedelics, psychosis and risk reduction [Audio podcast episode]. In Back from the abyss: Psychiatry in stories.
            </div>

            {/* Export Popup */}
            {showExportPopup && (
                <div className='export-popup-overlay'>
                    <div className='export-popup'>
                        <h3>Export Results</h3>
                        <p>Enter your name to export the psychboard results:</p>
                        <input
                            type='text'
                            value={exportName}
                            onChange={(e) => setExportName(e.target.value)}
                            placeholder='Enter your name...'
                            className='export-name-input'
                            autoFocus
                        />
                        <div className='export-popup-buttons'>
                            <button
                                className='export-confirm-button'
                                onClick={handleExportResults}
                                disabled={!exportName.trim()}
                            >
                                📊 Export Results
                            </button>
                            <button
                                className='export-cancel-button'
                                onClick={closeExportPopup}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Disclaimer */}
            <div className='disclaimer'>
                This software is not intended to be taken as a diagnosis or medical advise and is only for learning purposes. If you believe you have a medical condition, please contact a healthcare professional.
            </div>
        </div>
    );
}

export default PsychBoard